import type { SessionSummary as SummaryT } from "../lib/api";

interface Props {
  summary: SummaryT;
  onClose: () => void;
  teacherName: string;
}

function formatDuration(s: number): string {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return r === 0 ? `${m} min` : `${m} min ${r}s`;
}

function typeColor(type: string): string {
  if (type === "grammar" || type === "tense" || type === "word_order")
    return "bg-rose-500/15 text-rose-200 border-rose-500/40";
  if (type === "preposition" || type === "article")
    return "bg-amber-500/15 text-amber-200 border-amber-500/40";
  if (type === "vocab" || type === "spelling")
    return "bg-sky-500/15 text-sky-200 border-sky-500/40";
  if (type === "pronunciation")
    return "bg-fuchsia-500/15 text-fuchsia-200 border-fuchsia-500/40";
  return "bg-zinc-500/15 text-zinc-200 border-zinc-500/40";
}

export function SessionSummary({ summary, onClose, teacherName }: Props): JSX.Element {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl border border-ink-700 bg-ink-900 shadow-2xl">
        <header className="flex items-start justify-between border-b border-ink-700 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-white">Nice work!</h2>
            <p className="text-xs text-ink-400 mt-1">
              Session with {teacherName} · {formatDuration(summary.duration_s)} ·{" "}
              {summary.turn_count_user} {summary.turn_count_user === 1 ? "turn" : "turns"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg border border-ink-600 px-3 py-1 text-xs text-ink-400 hover:text-white"
          >
            Close
          </button>
        </header>

        <div className="grid grid-cols-3 gap-3 px-6 py-4 border-b border-ink-700">
          <Stat label="Errors" value={String(summary.error_count)} accent="rose" />
          <Stat
            label="New words"
            value={String(summary.new_vocab.length)}
            accent="lime"
          />
          <Stat
            label="Level"
            value={summary.estimated_level ?? "—"}
            accent="sky"
          />
        </div>

        <section className="px-6 py-4 border-b border-ink-700">
          <h3 className="text-xs uppercase tracking-widest text-ink-400 mb-3">
            What to focus on
          </h3>
          {summary.errors_by_category.length === 0 ? (
            <p className="text-sm text-emerald-300/90">
              No errors this session — excellent! Keep going.
            </p>
          ) : (
            <ul className="space-y-2">
              {summary.errors_by_category.map((e, i) => (
                <li
                  key={i}
                  className={`rounded-lg border px-3 py-2 text-sm ${typeColor(e.type)}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium">{e.category.replace(/-/g, " ")}</div>
                    <div className="font-mono text-[11px] opacity-70">
                      {e.type} {"·"} ×{e.count}
                    </div>
                  </div>
                  <div className="mt-1 text-zinc-200/90 text-xs">{e.example}</div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {summary.new_vocab.length > 0 && (
          <section className="px-6 py-4 border-b border-ink-700">
            <h3 className="text-xs uppercase tracking-widest text-ink-400 mb-2">
              Words you used
            </h3>
            <div className="flex flex-wrap gap-1.5">
              {summary.new_vocab.map((w) => (
                <span
                  key={w}
                  className="rounded-full border border-lime-500/40 bg-lime-500/10 px-2 py-0.5 text-xs text-lime-200"
                >
                  {w}
                </span>
              ))}
            </div>
          </section>
        )}

        {summary.top_recurring_errors.length > 0 && (
          <section className="px-6 py-4">
            <h3 className="text-xs uppercase tracking-widest text-ink-400 mb-2">
              Recurring across all your sessions
            </h3>
            <ul className="space-y-1.5">
              {summary.top_recurring_errors.map((e, i) => (
                <li
                  key={i}
                  className="rounded border border-ink-700 bg-ink-800/60 px-3 py-2 text-xs"
                >
                  <div className="flex items-center justify-between text-zinc-300">
                    <span className="font-medium">{e.category.replace(/-/g, " ")}</span>
                    <span className="font-mono opacity-70">×{e.count}</span>
                  </div>
                  <div className="mt-1 text-zinc-400">{e.explanation}</div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer className="px-6 py-4 border-t border-ink-700 flex justify-end">
          <button
            onClick={onClose}
            className="rounded-lg bg-lime-500 hover:bg-lime-400 text-ink-900 font-medium px-4 py-2 text-sm"
          >
            Done
          </button>
        </footer>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string;
  value: string;
  accent: "rose" | "lime" | "sky";
}): JSX.Element {
  const ring =
    accent === "rose"
      ? "border-rose-500/40 text-rose-200"
      : accent === "lime"
        ? "border-lime-500/40 text-lime-200"
        : "border-sky-500/40 text-sky-200";
  return (
    <div className={`rounded-xl border ${ring} bg-ink-800/40 px-3 py-2 text-center`}>
      <div className="text-2xl font-semibold">{value}</div>
      <div className="text-[10px] uppercase tracking-widest opacity-80 mt-1">{label}</div>
    </div>
  );
}
